//categories.ts

import { AccountType, EBITDARole, ChartAccount, NWCRole } from '../../types';

export interface CategoryOption {
  value: string;
  label: string;
  type: AccountType;
  fsType: 'balance-sheet' | 'income-statement';
  tagsInclude: string[];
  tagsExclude: string[];
  parentCategory?: string;
  nwcRole?: NWCRole;
  ebitdaRole?: EBITDARole;
  isNonCash?: boolean;
  isPpe?: boolean;
  isDebt?: boolean;
  isEquityCapital?: boolean;
}

export const categoryOptions: CategoryOption[] = [
  // N/A option
  { 
    value: 'N/A',
    label: 'N/A', 
    type: 'n/a', 
    fsType: 'balance-sheet',
    tagsInclude: [],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  
  // Current Assets
  { 
    value: 'Cash',
    label: 'Cash', 
    type: 'current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['cash', 'bank account', 'checking', 'savings', 'money market'],
    tagsExclude: ['over', 'short', 'loan', 'debt'],
    nwcRole: 'cash',
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Restricted Cash', 
    label: 'Restricted Cash', 
    type: 'current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['restricted cash'],
    tagsExclude: [],
    nwcRole: 'cash',
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Short-Term-Investments',
    label: 'Short-Term Investments', 
    type: 'current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['short term investment', 'marketable securities', 'trading securities', 'available for sale securities', 'investment securities', 'equity securities'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Trade Accounts Receivable',
    label: 'Trade Accounts Receivable', 
    type: 'current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['trade receivable', 'account receivable', 'accounts receivable', 'allowance for doubtful'],
    tagsExclude: [],
    nwcRole: 'accounts-receivable',
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Notes Receivable',
    label: 'Notes Receivable', 
    type: 'current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['note receivable', 'notes receivable'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Other Receivables',
    label: 'Other Receivables',
    type: 'current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['other receivable', 'unbilled receivable', 'accrued receivable', 'earned not billed'],
    tagsExclude: ['conditional'],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  {
    value: 'Contract Assets',
    label: 'Contract Assets',
    type: 'current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['contract asset', 'conditional unbilled receivable', 'performance obligation receivable'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Current Portion of Notes Receivable',
    label: 'Current Portion of Notes Receivable',
    type: 'current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['current notes receivable'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Current Intercompany Receivables',
    label: 'Current Intercompany Receivables',
    type: 'current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['current intercompany receivable', 'current interco receivable'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Current Portion of Right-of-use Asset',
    label: 'Current Portion of Right-of-use Asset',
    type: 'current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['current right of use asset', 'current rou asset', 'current portion right of use asset', 'current capital lease asset', 'short term right of use asset'],
    tagsExclude: [],
    isNonCash: true,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Inventory',
    label: 'Inventory', 
    type: 'current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['inventory', 'materials', 'supplies', 'electrical parts', 'wire', 'cable', 'components', 'transformers', 'switchgear'],
    tagsExclude: ['office', 'equity'],
    nwcRole: 'inventory',
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Prepaid Expenses',
    label: 'Prepaid Expenses',
    type: 'current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['prepaid'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Other Current Assets',
    label: 'Other Current Assets',
    type: 'current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['current asset'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  
  // Non-current Assets
  { 
    value: 'Long-Term-Investments',
    label: 'Long-Term Investments', 
    type: 'non-current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['long term investment', 'investment securities long', 'other investment', 'minority investment', 'less than 20 investment', 'passive investment', 'cost method investment', 'held to maturity'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Investment in Associates',
    label: 'Investment in Associates',
    type: 'non-current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['investment in associate', 'equity method investment'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Property, Plant & Equipment',
    label: 'Property, Plant & Equipment',
    type: 'non-current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['property', 'plant', 'equipment', 'capital asset', 'fixed asset', 'building improvement', 'leasehold improvement', 'machinery', 'construction in progress', 'land', 'vehicles', 'furniture', 'warehouse equipment', 'forklift', 'racking', 'software', 'accumulated depreciation', 'building', 'improvement', 'computer equipment', 'office equipment', 'shop equipment', 'production equipment', 'manufacturing equipment', 'test equipment', 'lab equipment', 'research equipment', 'development equipment'],
    tagsExclude: ['insurance', 'rental', 'expense', 'supplies', 'notes payable', 'note payable', 'payable'],
    isNonCash: false,
    isPpe: true,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Right-of-use Asset',
    label: 'Right-of-use Asset',
    type: 'non-current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['right of use asset', 'rou asset', 'accumulated amortization rou', 'accumulated amortization right'],
    tagsExclude: [],
    isNonCash: true,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Goodwill, Net',
    label: 'Goodwill, Net',
    type: 'non-current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['goodwill', 'accumulated amortization goodwill'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Other Intangible Assets, Net',
    label: 'Other Intangible Assets, Net',
    type: 'non-current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['intangible', 'accumulated amortization intangible', 'accumulated amortization'],
    tagsExclude: ['goodwill'],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  {
    value: 'Deferred Tax Assets',
    label: 'Deferred Tax Assets',
    type: 'non-current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['deferred tax asset'],
    tagsExclude: [],
    isNonCash: true,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  {
    value: 'Pension Plan Assets',
    label: 'Pension Plan Assets',
    type: 'non-current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['pension plan asset', 'plan investment'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  {
    value: 'Prepaid Pension Cost',
    label: 'Prepaid Pension Cost',
    type: 'non-current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['prepaid pension', 'pension prepayment'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  {
    value: 'Other Post-Employment Benefit Asset',
    label: 'Other Post-Employment Benefit Asset',
    type: 'non-current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['opeb asset', 'post employment benefit asset'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Long-term Notes Receivable',
    label: 'Long-term Notes Receivable',
    type: 'non-current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['notes receivable'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Non-current Intercompany Receivables',
    label: 'Non-current Intercompany Receivables',
    type: 'non-current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['intercompany receivable', 'interco receivable'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Equity-Method-Investments',
    label: 'Equity Method Investments', 
    type: 'non-current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['equity method investment', 'investment associate', 'investment joint venture', 'equity accounted investment', 'equity investment'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Other Long-term Assets',
    label: 'Other Long-term Assets',
    type: 'non-current-asset',
    fsType: 'balance-sheet',
    tagsInclude: ['long term asset'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  
  // Current Liabilities
  { 
    value: 'Trade Accounts Payable',
    label: 'Trade Accounts Payable',
    type: 'current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['trade payable', 'account payable', 'accounts payable', 'supplier payable', 'vendor payable'],
    tagsExclude: ['over', 'short'],
    nwcRole: 'accounts-payable',
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Other Accounts Payable',
    label: 'Other Accounts Payable',
    type: 'current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['other payable'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  {
    value: 'Contract Liabilities',
    label: 'Contract Liabilities',
    type: 'current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['contract liability', 'deferred revenue', 'unearned revenue'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Accrued Expenses',
    label: 'Accrued Expenses',
    type: 'current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['accrued'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Sales Tax Payable',
    label: 'Sales Tax Payable',
    type: 'current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['sales tax payable'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Income Tax Payable',
    label: 'Income Tax Payable',
    type: 'current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['income tax payable'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Short-term Debt',
    label: 'Short-term Debt',
    type: 'current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['short term debt', 'short term loan', 'short term note', 'short term borrowing', 'current portion debt', 'current debt', 'current loan', 'current note', 'line of credit', 'revolver', 'loc', 'current maturity'],
    tagsExclude: [],
    nwcRole: 'short-term-debt',
    isNonCash: false,
    isPpe: false,
    isDebt: true,
    isEquityCapital: false
  },
  { 
    value: 'Current Portion of Right-of-use Liability',
    label: 'Current Portion of Right-of-use Liability',
    type: 'current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['current right of use liability', 'current rou liability', 'current lease liability', 'current portion right of use liability', 'current portion rou liability', 'current portion lease liability', 'short term right of use liability', 'short term rou liability', 'short term lease liability', 'capital lease payable', 'current lease payable', 'current lease obligation'],
    tagsExclude: [],
    nwcRole: 'short-term-debt',
    isNonCash: true,
    isPpe: false,
    isDebt: true,
    isEquityCapital: false
  },
  { 
    value: 'Current Intercompany Payables',
    label: 'Current Intercompany Payables',
    type: 'current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['current intercompany payable', 'current interco payable'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Other Current Liabilities',
    label: 'Other Current Liabilities',
    type: 'current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['other liability'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  
  // Non-current Liabilities
  { 
    value: 'Long-term Debt',
    label: 'Long-term Debt',
    type: 'non-current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['long term debt', 'long term loan', 'long term note', 'long term borrowing', 'note payable', 'notes payable','loan'],
    tagsExclude: ['interco', 'intercompany', 'revolver', 'loc', 'line of credit', 'current', 'short'],
    isNonCash: false,
    isPpe: false,
    isDebt: true,
    isEquityCapital: false
  },
  { 
    value: 'Right-of-use Liability',
    label: 'Right-of-use Liability',
    type: 'non-current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['right of use liability', 'rou liability', 'lease liability'],
    tagsExclude: [],
    isNonCash: true,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Non-current Intercompany Payables',
    label: 'Non-current Intercompany Payables',
    type: 'non-current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['intercompany payable', 'interco payable'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  {
    value: 'Deferred Tax Liabilities',
    label: 'Deferred Tax Liabilities',
    type: 'non-current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['deferred tax liability'],
    tagsExclude: [],
    isNonCash: true,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  {
    value: 'Projected Benefit Obligation',
    label: 'Projected Benefit Obligation',
    type: 'non-current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['projected benefit obligation', 'pbo', 'pension obligation'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  {
    value: 'Accrued Pension Cost',
    label: 'Accrued Pension Cost',
    type: 'non-current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['accrued pension', 'pension accrual'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  {
    value: 'Additional Pension Liability',
    label: 'Additional Pension Liability',
    type: 'non-current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['additional pension liability', 'minimum pension liability'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  {
    value: 'Other Post-Employment Benefit Liability',
    label: 'Other Post-Employment Benefit Liability',
    type: 'non-current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['opeb liability', 'post employment benefit obligation'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Other Long-term Liabilities',
    label: 'Other Long-term Liabilities',
    type: 'non-current-liability',
    fsType: 'balance-sheet',
    tagsInclude: ['other liability'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  
  // Equity
  { 
    value: 'Common Stock',
    label: 'Common Stock',
    type: 'equity',
    fsType: 'balance-sheet',
    tagsInclude: ['common stock', 'capital stock', 'class a stock', 'class b stock', 'class c stock', 'voting stock', 'nonvoting stock', 'par value stock', 'stated value stock', 'ordinary shares'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: true
  },
  { 
    value: 'Additional Paid-in Capital',
    label: 'Additional Paid-in Capital',
    type: 'equity',
    fsType: 'balance-sheet',
    tagsInclude: ['additional paid in capital', 'paid in capital excess', 'apic', 'capital in excess par', 'capital in excess stated', 'paid in surplus', 'capital surplus', 'share premium', 'contributed surplus', 'paid in excess'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: true
  },
  { 
    value: 'Retained Earnings',
    label: 'Retained Earnings',
    type: 'equity',
    fsType: 'balance-sheet',
    tagsInclude: ['retained earnings'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Current Period Earnings',
    label: 'Current Period Earnings',
    type: 'equity',
    fsType: 'balance-sheet',
    tagsInclude: ['current earnings', 'current period earnings', 'current year earnings', 'net income', 'current period income', 'current year income', 'profit loss current', 'current profit loss', 'income current period'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Other Equity',
    label: 'Other Equity',
    type: 'equity',
    fsType: 'balance-sheet',
    tagsInclude: ['other equity'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: true
  },
  { 
    value: 'Dividends Paid',
    label: 'Dividends Paid',
    type: 'contra-equity',
    fsType: 'balance-sheet',
    tagsInclude: ['dividend'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: true
  },
  { 
    value: 'Treasury Stock',
    label: 'Treasury Stock',
    type: 'contra-equity',
    fsType: 'balance-sheet',
    tagsInclude: ['treasury stock', 'stock repurchase', 'share repurchase'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: true
  },
  
  // Revenue Categories
  { 
    value: 'Sales Revenue',
    label: 'Sales Revenue', 
    type: 'revenue',
    fsType: 'income-statement',
    tagsInclude: ['sales', 'revenue', 'income', 'wholesale', 'retail', 'trading'],
    tagsExclude: ['tax', 'salary', 'wage', 'return', 'discount'],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Sales Returns and Allowances',
    label: 'Sales Returns and Allowances', 
    type: 'contra-revenue',
    fsType: 'income-statement',
    tagsInclude: ['sales return', 'return allowance', 'damaged goods return'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Sales Discounts',
    label: 'Sales Discounts', 
    type: 'contra-revenue',
    fsType: 'income-statement',
    tagsInclude: ['sales discount'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  
  // Cost of Sales Categories
  { 
    value: 'Cost of Goods Sold',
    label: 'Cost of Goods Sold', 
    type: 'cogs',
    fsType: 'income-statement',
    tagsInclude: ['cost of goods sold', 'cogs', 'purchase price', 'merchandise cost', 'cost purchase', 'net purchase', 'purchases inventory', 'purchases', 'consignment purchase', 'purchase consignment'],
    tagsExclude: ['handling', 'freight', 'inbound', 'outbound'],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  {  
    value: 'Purchase Returns',
    label: 'Purchase Returns', 
    type: 'contra-cogs',
    fsType: 'income-statement',
    tagsInclude: ['purchase return', 'return outward'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Purchase Discounts',
    label: 'Purchase Discounts', 
    type: 'contra-cogs',
    fsType: 'income-statement',
    tagsInclude: ['purchase discount', 'discount purchase', 'vendor discount', 'early pay discount', 'prompt pay discount', 'cash discount purchase'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Vendor Rebates',
    label: 'Vendor Rebates', 
    type: 'contra-cogs',
    fsType: 'income-statement',
    tagsInclude: ['rebate', 'vendor rebate', 'manufacturer rebate', 'volume discount'],
    tagsExclude: [],
    isNonCash: false,
    isPpe: false,
    isDebt: false,
    isEquityCapital: false
  },
  { 
    value: 'Other Cost of Goods Sold',
    label: 'Other Cost of Goods Sold', 
    type: 'cogs',
    fsType: 'income-statement',
    tagsInclude: ['other cost of goods', 'inventory obsolescence', 'inventory reserve', 'inventory write down', 'inventory shrinkage', 'inventory loss', 'inventory adjustment', 'adjustment to inventory', 'periodic adjustment', 'year end adjustment', 'month end adjustment', 'quarter end adjustment', 'scrap expense', 'waste expense', 'freight in', 'inbound freight', 'handling in', 'handling cost', 'inbound handling', 'incoming handling', 'cogs handling', 'handling cogs', 'clearing comm', 'clearing freight', 'clearing purchase', 'clearing cost', 'clearing cogs', 'clearing inventory', 'cost difference', 'difference cost', 'cogs difference', 'difference cogs', 'restock charge'],
   tagsExclude: [],
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },
 
 // Operating Expense Categories
 { 
   value: 'Salaries and Benefits Expense',
   label: 'Salaries and Benefits Expense', 
   type: 'operating-expense',
   fsType: 'income-statement',
   tagsInclude: ['salary', 'salaries', 'wage', 'benefit', 'payroll', 'commission', 'health insurance', 'medical insurance', 'dental insurance', 'vision insurance', 'life insurance', 'disability insurance', '401k match', 'retirement contribution', 'retirement plan', 'pension contribution', 'pension plan', 'union dues pension', 'union contribution', 'union benefit'],
   tagsExclude: [],
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },
 { 
   value: 'Distribution and Shipping Expense',
   label: 'Distribution and Shipping Expense', 
   type: 'operating-expense',
   fsType: 'income-statement',
   tagsInclude: ['delivery', 'shipping', 'freight', 'fulfillment', 'postage', 'distribution'],
   tagsExclude: [],
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },
 { 
   value: 'General & Administrative Expense',
   label: 'General & Administrative Expense',
   type: 'operating-expense',
   fsType: 'income-statement',
   tagsInclude: ['general', 'administrative', 'g&a', 'office supplies', 'admin supplies', 'it supplies', 'computer supplies', 'general supplies', 'misc supplies', 'maintenance supplies', 'janitorial supplies', 'cleaning supplies', 'kitchen supplies', 'break room supplies', 'dues subscriptions', 'miscellaneous office', 'insurance expense', 'workers comp insurance', 'property insurance', 'casualty insurance', 'vehicle insurance', 'auto expense', 'vehicle expense', 'fleet expense', 'car expense', 'mileage expense', 'fuel expense', 'toll expense', 'meals entertainment', 'entertainment expense', 'meals expense', 'training', 'education', 'professional development', 'bank fee', 'bank charge', 'bank service', 'service fee', 'service charge', 'merchant fee', 'payment processing', 'credit card fee', 'wire fee', 'ach fee', 'transaction fee'],
   tagsExclude: ['sales training', 'sales education', 'sales professional development'],
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },
 { 
   value: 'Rent and Facilities Expense',
   label: 'Rent and Facilities Expense',
   type: 'operating-expense',
   fsType: 'income-statement',
   tagsInclude: ['warehouse expense', 'warehouse maintenance', 'handling equipment', 'storage expense', 'rent expense', 'lease expense', 'equipment rental', 'utility', 'utilities', 'electricity', 'electric expense', 'gas', 'gas expense', 'gas and electric', 'electric and gas', 'water', 'water expense', 'sewer', 'sewer expense', 'waste removal', 'power', 'power expense', 'energy', 'energy expense'],
   tagsExclude: ['delivery equipment rental'],
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },
 { 
   value: 'Sales and Marketing Expense',
   label: 'Sales and Marketing Expense',
   type: 'operating-expense',
   fsType: 'income-statement',
   tagsInclude: ['sales expense', 'marketing', 'advertising', 'catalog', 'trade show', 'sample expense', 'travel sales', 'entertainment sales', 'sales travel', 'selling travel', 'sales entertainment', 'selling entertainment'],
   tagsExclude: [],
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 }, 
 { 
   value: 'Professional Services Expense',
   label: 'Professional Services Expense', 
   type: 'operating-expense',
   fsType: 'income-statement',
   tagsInclude: ['professional', 'consulting', 'legal', 'accounting', 'audit', 'tax service', 'advisor'],
   tagsExclude: [],
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },
 { 
   value: 'Research and Development Expense',
   label: 'Research and Development Expense', 
   type: 'operating-expense',
   fsType: 'income-statement',
   tagsInclude: ['research', 'development', 'r&d', 'product development', 'engineering', 'prototype', 'innovation'],
   tagsExclude: [],
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },
 {
   value: 'Technology and Software Expense',
   label: 'Technology and Software Expense', 
   type: 'operating-expense',
   fsType: 'income-statement',
   tagsInclude: ['software', 'subscription', 'it', 'information technology', 'computer', 'web hosting', 'domain', 'saas', 'cloud'],
   tagsExclude: [],
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },  
 { 
   value: 'Training and Development Expense',
   label: 'Training and Development Expense', 
   type: 'operating-expense',
   fsType: 'income-statement',
   tagsInclude: ['training', 'education', 'professional development', 'seminar', 'conference', 'certification', 'course'],
   tagsExclude: [],
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },
 { 
   value: 'Travel and Entertainment Expense',
   label: 'Travel and Entertainment Expense', 
   type: 'operating-expense',
   fsType: 'income-statement',
   tagsInclude: ['travel', 'entertainment', 'meals', 'lodging', 'hotel', 'airfare', 'transportation', 'mileage'],
   tagsExclude: [],
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },
 { 
   value: 'Repairs and Maintenance Expense',
   label: 'Repairs and Maintenance Expense', 
   type: 'operating-expense',
   fsType: 'income-statement',
   tagsInclude: ['repair', 'maintenance', 'servicing', 'upkeep', 'equipment repair', 'facility repair', 'building repair', 'vehicle repair', 'fleet repair'],
   tagsExclude: [],
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },
 { 
   value: 'Bad Debt Expense',
   label: 'Bad Debt Expense',
   type: 'operating-expense',
   fsType: 'income-statement',
   tagsInclude: ['bad debt'],
   tagsExclude: [],
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },
 { 
   value: 'Charitable Contributions Expense',
   label: 'Charitable Contributions Expense',
   type: 'operating-expense',  
   fsType: 'income-statement',
   tagsInclude: ['charitable', 'donation', 'contribution', 'benevolent', 'philanthropy'],
   tagsExclude: ['revenue', 'income'],
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },
 { 
   value: 'Other Operating Expense (Income)',
   label: 'Other Operating Expense (Income)',
   type: 'operating-expense',
   fsType: 'income-statement',
   tagsInclude: ['other operating expense', 'cash over', 'cash short', 'over cash', 'short cash', 'over receipt', 'short receipt', 'receipt over', 'receipt short', 'payable over', 'payable short', 'payment over', 'payment short', 'over payable', 'short payable', 'over payment', 'short payment', 'miscellaneous expense'],
   tagsExclude: [],
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },
 
 // D&A Categories
 { 
   value: 'Depreciation',
   label: 'Depreciation', 
   type: 'operating-expense',
   fsType: 'income-statement',
   tagsInclude: ['depreciation', 'depreciation expense', 'depreciation building', 'depreciation equipment', 'depreciation vehicle', 'depreciation machinery', 'depreciation furniture', 'depreciation computer', 'depreciation office', 'depreciation warehouse', 'depreciation plant', 'depreciation property'],
   tagsExclude: ['accumulated'],
   ebitdaRole: 'depreciation',
   isNonCash: true,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },
 { 
   value: 'Amortization',
   label: 'Amortization', 
   type: 'operating-expense',
   fsType: 'income-statement',
   tagsInclude: ['amortization'],
   tagsExclude: ['accumulated', 'accum'],
   ebitdaRole: 'amortization',
   isNonCash: true,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },
 
 // Other Non-operating Expenses
 {
   value: 'Non-Recurring Items',
   label: 'Non-Recurring Items',
   type: 'other-expense',
   fsType: 'income-statement',
   tagsInclude: ['non recurring', 'one time', 'extraordinary', 'unusual', 'special item', 'restructuring'],
   tagsExclude: [],
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },
 { 
   value: 'Other Non-operating Expenses (Income)',
   label: 'Other Non-operating Expenses (Income)', 
   type: 'other-expense',
   fsType: 'income-statement',
   tagsInclude: ['other expense', 'other income', 'non operating expense', 'non operating income'],
   tagsExclude: [],
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },
 { 
   value: 'Interest Expense (Income)',
   label: 'Interest Expense (Income)', 
   type: 'interest-expense',
   fsType: 'income-statement',
   tagsInclude: ['interest expense', 'interest income'],
   tagsExclude: [],
   ebitdaRole: 'interest-expense',
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 },
 { 
   value: 'Income Tax Expense',
   label: 'Income Tax Expense', 
   type: 'income-tax-expense',
   fsType: 'income-statement',
   tagsInclude: ['income tax expense', 'state tax expense', 'corporate tax expense', 'tax expense income', 'tax expense state', 'tax expense corporate', 'state income tax'],
   tagsExclude: [],
   ebitdaRole: 'income-tax-expense',
   isNonCash: false,
   isPpe: false,
   isDebt: false,
   isEquityCapital: false
 }
];

// Get category by value
export const getCategoryByValue = (value: string): CategoryOption | undefined => {
 return categoryOptions.find(opt => opt.value === value);
};

// Helper function to get categories by type
export const getCategoriesByType = (types: AccountType[]): string[] => {
 return categoryOptions
   .filter(option => types.includes(option.type))
   .map(option => option.label);
};

// Helper function to get category type
export const getCategoryType = (categoryLabel: string): AccountType => {
 const category = categoryOptions.find(opt => opt.label === categoryLabel);
 return category?.type || 'n/a';
};

// Helper function to get financial statement type
export const getFSType = (categoryLabel: string): 'balance-sheet' | 'income-statement' => {
 const category = categoryOptions.find(opt => opt.label === categoryLabel);
 return category?.fsType || 'balance-sheet';
}

// Updated helper function to suggest category based on tags
export const suggestCategory = (accountName: string, templateCategories?: CategoryOption[]): string => {
 if (!accountName) return 'N/A';
 
 // Use template categories if available, otherwise fall back to default categoryOptions
 const categoriesToUse = templateCategories && templateCategories.length > 0 ? templateCategories : categoryOptions;
 
 const accountNameLower = accountName.toLowerCase();
 
 for (const category of categoriesToUse) {
   // Check if category has the required structure
   if (!category.tagsInclude || !category.tagsExclude) {
     continue;
   }
   
   // Check if account name includes any of the required tags
   const hasIncludedTag = category.tagsInclude.some(tag => 
     accountNameLower.includes(tag.toLowerCase())
   );
   
   // Check if account name excludes any forbidden tags
   const hasExcludedTag = category.tagsExclude.some(tag => 
     accountNameLower.includes(tag.toLowerCase())
   );
   
   if (hasIncludedTag && !hasExcludedTag) {
     return category.value;  // Return the value, not label, for consistency
   }
 }
 
 return 'N/A';
};

export const generateCategorySuggestions = (
  accounts: ChartAccount[],
  templateCategories: CategoryOption[] | undefined  // Allow undefined
) => {
  const accountsToProcess = accounts.filter(account => 
    !account.category || account.category === 'N/A'
  );

  if (accountsToProcess.length > 0) {
    const suggestions = accountsToProcess
      .map(account => ({
        accountNumber: account.accountNumber,
        category: templateCategories ? 
          suggestCategoryFromTemplate(account.name, templateCategories) : 
          suggestCategory(account.name)  // Fallback to default suggestions
      }))
      .filter(suggestion => suggestion.category !== 'N/A');
    
    return suggestions;
  }
  
  return [];
};

// Helper function to suggest category using template categories
const suggestCategoryFromTemplate = (accountName: string, templateCategories: CategoryOption[] | undefined): string => {
 if (!accountName) return 'N/A';
 
 // CRITICAL FIX: Check if templateCategories is defined
 if (!templateCategories || templateCategories.length === 0) {
   return 'N/A';
 }
 
 const accountNameLower = accountName.toLowerCase();
 
 for (const category of templateCategories) {
   // Additional safety check for category structure
   if (!category.tagsInclude || !category.tagsExclude) {
     continue;
   }
   
   const hasIncludedTag = category.tagsInclude.some(tag => 
     accountNameLower.includes(tag.toLowerCase())
   );
   
   const hasExcludedTag = category.tagsExclude.some(tag => 
     accountNameLower.includes(tag.toLowerCase())
   );
   
   if (hasIncludedTag && !hasExcludedTag) {
     return category.value;  // Return the value, not label
   }
 }
 
 return 'N/A';
};